import type { RouteRecordRaw } from 'vue-router'
import { getIsV2 } from '@/composables/use-design-version'

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'root',
        redirect: '/auth',
    },
    {
        path: '/404',
        name: '404',
        component: () => import('@/components/layouts/404.vue'),
    },
    {
        name: 'auth',
        path: '/auth',
        redirect: '/auth/sign-in',
        component: () => import('@/components/layouts/Auth.vue'),
        children: [
            {
                path: '/auth/sign-in',
                name: 'auth-sign-in',
                component: () => import('@/pages/auth/SignIn.vue'),
            },
            {
                path: '/auth/error',
                name: 'auth-error',
                component: () => import('@/pages/auth/Error.vue'),
            },
        ],
    },
    {
        name: 'dashboard',
        path: '/dashboard',
        component: () => getIsV2()
            ? import('@/components/layouts/Panel.vue')
            : import('@/components/layouts/Panel.v1.vue'),
        meta: {
            authRequired: true,
        },
        children: [
            {
                path: 'organization',
                name: 'settings-organization',
                component: () => import('@/pages/dashboard/Settings/Organization.vue'),
                children: [
                    {
                        path: 'members',
                        name: 'settings-organization-members',
                        component: () => import('@/pages/dashboard/Settings/OrganizationMembership.vue'),
                    },
                    {
                        path: 'invitations',
                        name: 'settings-organization-invitations',
                        component: () => import('@/pages/dashboard/Settings/OrganizationInvitations.vue'),
                    },
                    {
                        path: 'tokens',
                        name: 'settings-organization-tokens',
                        component: () => import('@/pages/dashboard/Settings/OrganizationTokens.vue'),
                    },
                    {
                        path: 'domains',
                        name: 'settings-organization-domains',
                        component: () => import('@/pages/dashboard/Settings/OrganizationDomains.vue'),
                    },
                    {
                        path: 'assets',
                        name: 'settings-organization-assets',
                        component: () => import('@/pages/dashboard/Settings/OrganizationAssets.vue'),
                    },
                ],
            },
            {
                path: 'workspace/:workspaceId',
                name: 'workspace-main',
                children: [
                    {
                        path: 'settings',
                        name: 'workspace-settings-home',
                        children: [
                            {
                                path: '',
                                name: 'workspace-settings',
                                component: () => import('@/pages/dashboard/Settings/Workspace.vue'),
                            },
                            {
                                path: 'membership',
                                name: 'workspace-membership',
                                component: () => import('@/pages/dashboard/Settings/WorkspaceMembership.vue'),
                            },
                            {
                                path: 'membership/roles',
                                name: 'workspace-roles',
                                component: () => import('@/pages/dashboard/Settings/WorkspaceRoles.vue'),
                            },
                            {
                                path: 'webhook',
                                name: 'settings-webhook',
                                component: () => import('@/pages/dashboard/Settings/Webhook.vue'),
                            },
                            {
                                path: 'custom-table',
                                name: 'custom-table-home',
                                component: () => import('@/pages/dashboard/Settings/CustomTable/Index.vue'),
                                children: [
                                    {
                                        path: '',
                                        name: 'custom-table',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/List.vue'),
                                    },
                                    {
                                        path: 'add',
                                        name: 'custom-table-add',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/EditCustomTable.vue'),
                                    },
                                    {
                                        path: 'import',
                                        name: 'custom-tables-import',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/ImportCustomTable.vue'),
                                    },
                                    {
                                        path: 'export',
                                        name: 'custom-tables-export',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/ExportCustomTable.vue'),
                                    },
                                    {
                                        path: 'graph-view/:graphViewId?',
                                        name: 'custom-table-configure-graph-view',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/ConfigureGraphView.vue'),
                                    },
                                    {
                                        path: ':tableId',
                                        name: 'custom-table-edit',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/EditCustomTable.vue'),
                                        children: [
                                            {
                                                path: 'field/:customTableFieldId',
                                                name: 'custom-table-field-edit',
                                                component: () => import('@/pages/dashboard/Settings/CustomTable/EditField.vue'),
                                            },
                                            {
                                                path: 'relation/:customTableRelationId',
                                                name: 'custom-table-relation-edit',
                                                component: () => import('@/pages/dashboard/Settings/CustomTable/EditRelation.vue'),
                                            },
                                            {
                                                path: 'configure-record-view/:recordViewId?',
                                                name: 'custom-table-configure-record-view',
                                                component: () => import('@/pages/dashboard/Settings/CustomTable/ConfigureRecordView.vue'),
                                            },
                                        ],
                                    },
                                    {
                                        path: 'docs',
                                        name: 'custom-table-api-docs-home',
                                        component: () => import('@/pages/dashboard/Settings/CustomTable/CustomTableApiDocs.vue'),
                                        meta: {
                                            layout: 'full-view',
                                        },
                                        children: [
                                            {
                                                path: ':customTableId',
                                                name: 'custom-table-docs',
                                                component: () => import('@/pages/dashboard/Settings/CustomTable/CustomTableApiDocs.vue'),
                                                meta: {
                                                    layout: 'full-view',
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'analytics-reports',
                                children: [
                                    {
                                        path: '',
                                        name: 'settings-analytics-reports',
                                        component: () => import('@/pages/dashboard/Settings/AnalyticsReports/ListAnalyticsReports.vue'),
                                    },
                                    {
                                        path: 'add',
                                        name: 'settings-analytics-reports-add',
                                        component: () => import('@/pages/dashboard/Settings/AnalyticsReports/EditAnalyticsReport.vue'),
                                    },
                                    {
                                        path: ':reportId',
                                        name: 'settings-analytics-reports-edit',
                                        component: () => import('@/pages/dashboard/Settings/AnalyticsReports/EditAnalyticsReport.vue'),
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'overview',
                        name: 'workspace-overview',
                        component: () => import('@/pages/dashboard/WorkspaceOverview.vue'),
                    },
                    {
                        path: 'workflows',
                        name: 'workspace-workflows',
                        component: () => import('@/pages/dashboard/Workflows/Index.vue'),
                        children: [
                            {
                                path: ':workflowId',
                                name: 'workspace-workflow',
                                component: () => import('@/pages/dashboard/Workflows/Workflow.vue'),
                                children: [{
                                    path: '',
                                    name: 'workspace-workflows-edit',
                                    component: () => import('@/pages/dashboard/Workflows/Edit.vue'),
                                }],
                            },
                            {
                                path: 'run/:runId',
                                name: 'workflow-run',
                                component: () => import('@/pages/dashboard/Workflows/RunDetails.vue'),
                            },
                            {
                                path: 'execution/:runId',
                                name: 'workflow-execution',
                                component: () => import('@/pages/dashboard/Workflows/WorkflowExecutionDetails.vue'),
                            },
                        ],
                    },
                    {
                        path: 'analytics-reports',
                        name: 'workspace-analytics-reports',
                        children: [
                            {
                                path: ':reportId',
                                name: 'workspace-analytics-report',
                                component: () => import('@/pages/dashboard/AnalyticsReports/Report.vue'),
                            },
                        ],
                    },
                    {
                        path: 'custom-table/:customTableId',
                        children: [
                            {
                                path: ':customTableViewId?',
                                name: 'custom-table-view',
                                component: () => import('@/pages/dashboard/CustomTables/Index.vue'),
                            },
                            {
                                path: 'details/:recordId',
                                name: 'custom-table-record-view',
                                component: () => import('@/pages/dashboard/CustomTables/RecordView.vue'),
                            },
                            {
                                path: 'export',
                                name: 'custom-table-export',
                                component: () => import('@/pages/dashboard/CustomTables/ExportContainer.vue'),
                            },
                            {
                                path: 'import/:importId?',
                                name: 'custom-table-import',
                                component: () => import('@/pages/dashboard/CustomTables/Import.vue'),
                            },
                            {
                                path: 'import-history',
                                name: 'custom-table-import-history',
                                component: () => import('@/pages/dashboard/CustomTables/ImportHistoryContainer.vue'),
                            },
                        ],
                    },
                    {
                        path: 'incidents',
                        children: [
                            {
                                path: '',
                                name: 'workspace-incidents',
                                component: () => import('@/pages/dashboard/Incidents/Index.vue'),
                                meta: {
                                    layout: 'full-view',
                                },
                            },
                        ],
                    },
                    {
                        path: 'insights/:insightId?',
                        name: 'workspace-insights',
                        component: () => import('@/pages/dashboard/Insights/Details.vue'),
                    },
                ],
            },
        ],
    },
    {
        path: '/workspace/:workspaceId/record-activity-log-print',
        name: 'custom-table-record-activity-log-print',
        component: () => import('@/pages/dashboard/CustomTables/ActivityLogPrint.vue'),
        meta: {
            authRequired: true,
        },
    },
    ...import.meta.env.DEV ? [
        {
            path: '/test',
            name: 'test',
            component: () => import('@/pages/dashboard/Test.vue'),
        },
        {
            path: '/prime-style-guide',
            name: 'prime-style-guide',
            component: () => import('@/pages/dashboard/PrimeStyleGuide.vue'),
        },
    ] : [],
]

export default routes
